import { Button, Group, TextInput } from '@mantine/core';
import { IconPlus, IconVideo, IconWand } from '@tabler/icons-react';
import { nanoid } from 'nanoid';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InstructionCreationSection } from 'src/Components/Forms/Instruction/InstructionForm/InstructionFormSection';
import { PhotoAttachmentsField } from 'src/Components/Forms/PhotoAttachmentsField';
import { UploadFileButton } from 'src/Components/Inputs/UploadFileButton';
import { useFetchStorageItemInformation } from 'src/Hooks/Mutations/useFetchStorageItemInformation';
import { useGenerateInstruction } from 'src/Hooks/Mutations/useGenerateInstruction';
import { useFileUploadManager } from 'src/Hooks/Shared/useFileUploadManager';
import { SectionsDto } from 'src/Services/API/generated';

import styles from './InstructionForm.module.scss';
import { Props } from './InstructionForm.types';

const InstructionFormComponent = ({
  onCancel,
  form,
  onSubmit,
  submitButtonLabel,
  removeSection,
  addSection,
}: Props) => {
  const { mutate: generate, isLoading: isGenerationLoading } = useGenerateInstruction({
    onSuccess: (data: SectionsDto) => {
      form.setFieldValue('title', data.title);
      form.setFieldValue('sections', []);

      data.sections.forEach(section => {
        form.insertListItem('sections', {
          title: section.title,
          content: section.content,
          localId: nanoid(),
          attachments: [],
        });
      });
    },
  });

  const { t } = useTranslation();
  const sections = form.values.sections.map((section, index) => (
    <InstructionCreationSection
      key={section.localId}
      index={index}
      form={form}
      removeSection={() => removeSection(index)}
    />
  ));

  // The main photo has its own upload: both keep the form busy, as when they shared one.
  const [isPhotoUploading, setIsPhotoUploading] = useState(false);
  const { startUpload, isLoading } = useFileUploadManager({
    onUploadReady: readyFile => {
      if (!readyFile.fileData) {
        console.error('readyFile.fileData should not be undefined.');
        return;
      }

      const attachment = {
        video: readyFile.videoData,
        // TODO : if id is not defined here for video, try readyFile.videoData?.fileId
        fileId: readyFile.fileData.id,
        type: readyFile.fileData.type,
        name: readyFile.fileData.name,
        previewUrl: readyFile.fileData!.urls?.previewThumbnailUrl,
      };

      form.setFieldValue('attachment', attachment);
    },
  });

  const { mutate: fetchFileInfo } = useFetchStorageItemInformation({
    onSuccess: fileInfo => {
      form.setFieldValue('attachment', {
        video: fileInfo.video,
        fileId: fileInfo.id,
        type: fileInfo.type,
        name: fileInfo.name,
        previewUrl: fileInfo.urls?.previewThumbnailUrl,
      });
    },
  });

  return (
    <form id="create-instruction-form" onSubmit={onSubmit} className={styles.fullFormContainer}>
      <TextInput
        placeholder={t(
          'instructions.creationTitle',
          'Titre de la consigne (exemple : Démarrer la chaudière)'
        )}
        {...form.getInputProps('title')}
      />
      <Group align={'center'} className={styles.topButtons}>
        <Button
          leftSection={<IconWand size={20} />}
          variant={'light'}
          onClick={() => generate(form.values.title)}
          loading={isGenerationLoading}
          disabled={form.values.title.length < 4}
        >
          {t('instructions.generate', "Générer la consigne avec l'IA")}
        </Button>
        <div className={styles.or}>{t('instruction.or', 'ou')}</div>
        <UploadFileButton
          onFileSelected={file => startUpload([file])}
          onExistingFileSelect={fileId => fetchFileInfo(fileId)}
          loading={isLoading || isPhotoUploading}
          single
          label={t('instructions.videoGenerate', 'Générer la consigne par vidéo')}
          size={'sm'}
          leftSection={<IconVideo size={20} />}
          className={''}
          variant={'light'}
          modalTitle={t('instructions.video.modalTitle', 'Ajouter une vidéo')}
          fileUploadLabel={t('instructions.video.uploadLabel', 'Importer une vidéo')}
          existingFileLabel={t('instructions.video.existingLabel', 'Lier une vidéo existante')}
        />
      </Group>
      <div className={styles.imageZone}>
        <PhotoAttachmentsField
          multiple={false}
          imagesOnly={false}
          value={form.values.attachment ? [form.values.attachment] : []}
          onChange={([attachment]) => form.setFieldValue('attachment', attachment)}
          loading={isLoading}
          onUploadingChange={setIsPhotoUploading}
          label={t(
            'instructions.creation.mainImage.label',
            'Ajouter la photo principale de la consigne'
          )}
          modalTitle={t(
            'instructions.creation.mainImage.modalTitle',
            'Ajouter la photo principale'
          )}
          fileUploadLabel={t('instructions.creation.mainImage.uploadLabel', 'Importer une photo')}
          existingFileLabel={t(
            'instructions.creation.mainImage.existingLabel',
            'Lier une photo existante'
          )}
        />
      </div>
      <div className={styles.sections}>{sections}</div>
      <Button variant={'transparent'} onClick={addSection} leftSection={<IconPlus size={20} />}>
        {t('instructions.addSection', 'Ajouter une étape')}
      </Button>
      <div className={styles.border} />
      <div className={styles.buttonsContainer}>
        <Button onClick={onCancel} variant="outline" className="btn-medium">
          {t('instructions.cancel', 'Annuler')}
        </Button>
        <Button type="submit" variant="primary" disabled={isLoading || isPhotoUploading}>
          {submitButtonLabel}
        </Button>
      </div>
    </form>
  );
};

export default InstructionFormComponent;
